/**
 * sanitizeOnPremFilename — frontend ESM port of the on-prem form-PDF filename sanitizer.
 *
 * CANONICAL SOURCE: teamio-lambda-layer/dependencies_source_for_layer/common.js
 *   → function sanitizeOnPremFilename(name, fallback = 'Patient Form')  (deployed to /opt/common)
 *
 * ⚠️ THIS FILE MUST STAY IN SYNC WITH THAT LAYER FUNCTION. The backend
 * (forms-service/forms-api.js + react-pdf-service.js) is what actually sanitizes the
 * staff-configured `formConfig.pdfFileName` before it becomes an S3 key, a Windows
 * filename in the OpenDental imaging folder, and a SQL literal in the on-prem EXE's
 * INSERT. This copy exists ONLY to PREVIEW that same transformation in the Form Builder
 * so staff see what their PDF will be saved as — it never replaces the backend sanitize.
 * If the layer function changes, change this port to match (canonical = layer), or the
 * preview silently drifts from what actually lands on disk.
 *
 * ESM here / CommonJS there: webpack can't run CommonJS under src/ (runtime crash) and
 * the layer copy must be require()-able — so the export style differs while the behavior
 * is kept identical. Behavior is pinned by cypress/e2e/forms-onprem-filename-sanitize.cy.js.
 *
 * Idempotent — sanitising an already-sanitised name is a no-op.
 */

// Windows refuses these as filenames regardless of extension (CON.pdf is still CON).
const WINDOWS_RESERVED_BASENAME = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;
// Leaves headroom under Windows' 260-char path limit for the OD imaging folder + "_MM-DD-YYYY.pdf".
const MAX_ONPREM_FILENAME_LENGTH = 120;

/**
 * @param {string} name      raw staff-configured name, e.g. "Consent: Ortho"
 * @param {string} [fallback] used when the name is empty or sanitises away entirely
 * @returns {string} a name safe as a Windows filename, a single S3 key segment, and a SQL literal
 */
export function sanitizeOnPremFilename(name, fallback = 'Patient Form') {
  if (name === null || name === undefined) return fallback;

  let s = String(name)
    .replace(/[<>:"/\\|?*]/g, '-')   // illegal in Windows filenames; '/' would also split the S3 key
    // '%' and '+' are legal on Windows, but the EXE percent-decodes S3 keys on its SMS path, so
    // stripping them here means an on-prem-bound name is safe on EITHER EXE path.
    .replace(/[%+]/g, '-')
    // Whitespace collapse MUST precede the control-character strip: tabs and newlines are both
    // whitespace AND control characters, so stripping first would glue words together
    // ("Post\tOp" -> "PostOp" instead of "Post Op").
    .replace(/\s+/g, ' ')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f\x7f]/g, '') // any remaining (non-whitespace) control characters
    .replace(/'/g, '')               // the EXE interpolates this into a SQL string literal
    .replace(/-{2,}/g, '-')
    .trim();

  // Windows silently strips trailing dots and spaces, so a name ending in one would not match
  // the file that actually lands on disk.
  s = s.replace(/[. ]+$/, '');

  if (s.length > MAX_ONPREM_FILENAME_LENGTH) {
    s = s.substring(0, MAX_ONPREM_FILENAME_LENGTH).trim().replace(/[. ]+$/, '');
  }
  if (WINDOWS_RESERVED_BASENAME.test(s)) s = `${s}-form`;

  // A name made only of separators ("///" -> "-") carries no meaning as a document name.
  if (/^[-. ]*$/.test(s)) return fallback;

  return s;
}

/**
 * sanitizeOnPremAttachmentName — extension-preserving variant for PATIENT-UPLOADED files.
 *
 * CANONICAL SOURCE: teamio-lambda-layer/dependencies_source_for_layer/common.js
 *   → function sanitizeOnPremAttachmentName(name, fallback = 'Patient Attachment')
 * ⚠️ Keep in sync with that layer function (same rule as the port above).
 *
 * The BACKEND is what actually protects the on-prem EXE — forms-service sanitizes attachment
 * names on the send path, which also covers submissions stored before this shipped. This copy
 * runs at upload time purely so the patient and the office see the name the file will really
 * be saved under, instead of it changing silently between upload and OpenDental.
 *
 * Why not reuse sanitizeOnPremFilename directly: it protects a PDF *base* name (the EXE appends
 * ".pdf" itself), so its 120-char cap would truncate a real extension away and leave an
 * unopenable file in OD imaging.
 *
 * @param {string} name       patient-supplied filename, e.g. "Driver's License.jpg"
 * @param {string} [fallback] used when the name is empty or sanitises away entirely
 * @returns {string} safe as a Windows filename, an OD `document.FileName`, and a SQL literal
 */
export function sanitizeOnPremAttachmentName(name, fallback = 'Patient Attachment') {
  if (name === null || name === undefined) return fallback;

  const raw = String(name);

  // Only a short alphanumeric tail counts as an extension. "Notes.Final Copy" is a name, not a
  // typed file, and must not get extension protection under the length cap.
  const match = raw.match(/^(.*)(\.[A-Za-z0-9]{1,8})$/);
  const rawBase = match ? match[1] : raw;
  const ext = match ? match[2] : '';   // case preserved — renaming surprises staff

  const SENTINEL = '\u0000EMPTY';
  let base = sanitizeOnPremFilename(rawBase, SENTINEL);
  if (base === SENTINEL) base = '';

  // Reserve room for the extension so the cap can never eat it.
  const maxBase = MAX_ONPREM_FILENAME_LENGTH - ext.length;
  if (base.length > maxBase) {
    base = base.substring(0, maxBase).trim().replace(/[. ]+$/, '');
  }

  if (base === '') return ext ? `${fallback}${ext}` : fallback;
  return `${base}${ext}`;
}
